import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchBills,
  selectBills,
  selectBillsLoading,
  selectBillsError,
} from "../../../../ReduxSlices/Bills/billSlice";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import DatePicker from "react-datepicker";
import Select from "react-select";
import { format } from "date-fns";
import LoadingDots from "../../../shared/LoadingDots";
import { baseSelectStyles } from "../../../shared/selectStyles";

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#A569BD", "#F1948A"];

const filterOptions = [
  { value: "today", label: "Today" },
  { value: "month", label: "This Month" },
  { value: "year", label: "This Year" },
  { value: "custom", label: "Specific Day" },
  { value: "range", label: "Date Range" },
];

const CustomInput = React.forwardRef(({ value, onClick, placeholder }, ref) => (
  <input
    ref={ref}
    value={value ? format(new Date(value), "do MMM, yyyy") : ""}
    onClick={onClick}
    readOnly
    placeholder={placeholder}
    className="date-picker-input"
     style={{
      marginLeft: "10px",
      backgroundColor: "#E9F7EE",
      border: "1px solid #A5D6A7",
      borderRadius: "8px",
      padding: "0 4px",
      height: "30px",
      fontSize: "13px",
      cursor: "pointer",
    }}
  />
));

const BillsReport = () => {
  const dispatch = useDispatch();
  const bills = useSelector(selectBills);
  const loading = useSelector(selectBillsLoading);
  const error = useSelector(selectBillsError);

  const [filter, setFilter] = useState("month");
  const [customDate, setCustomDate] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Fetch once if empty
  useEffect(() => {
    if (!bills || bills.length === 0) {
      dispatch(fetchBills());
    }
  }, [dispatch, bills.length]);

  // Filter bills by date
  const filteredBills = useMemo(() => {
    const now = new Date();
    return bills.filter((bill) => {
      const billDate = new Date(bill.created_at);
      switch (filter) {
        case "today":
          return billDate.toDateString() === now.toDateString();
        case "month":
          return (
            billDate.getMonth() === now.getMonth() &&
            billDate.getFullYear() === now.getFullYear()
          );
        case "year":
          return billDate.getFullYear() === now.getFullYear();
        case "custom":
          return customDate
            ? billDate.toDateString() === new Date(customDate).toDateString()
            : true;
        case "range":
          if (!startDate || !endDate) return false;
          const start = new Date(startDate);
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          return billDate >= start && billDate <= end;
        default:
          return true;
      }
    });
  }, [bills, filter, customDate, startDate, endDate]);

  // Compute totals per service
  const chartData = useMemo(() => {
    const totals = filteredBills.reduce((acc, bill) => {
      if (bill.status !== "paid") return acc;
      acc[bill.category] = (acc[bill.category] || 0) + parseFloat(bill.total_amount);
      return acc;
    }, {});
    return Object.entries(totals).map(([name, value]) => ({ name, value }));
  }, [filteredBills]);

  // Download PDF (unchanged)
  const downloadPDF = async () => {
    try {
      const queryParams = new URLSearchParams({ filter });
      if (filter === "custom" && customDate) queryParams.append("customDate", customDate);
      else if (filter === "range" && startDate && endDate) {
        queryParams.append("startDate", startDate);
        queryParams.append("endDate", endDate);
      }

      const res = await fetch(`/api/v1/admin/reports/total_bills_pdf?${queryParams.toString()}`, {
        method: "GET",
        credentials: "include",
      });

      if (!res.ok) throw new Error("Failed to download PDF");

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `total_bills_${filter}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("PDF download failed:", err);
    }
  };

  if (loading) return <LoadingDots />;
  if (error) return <p className="text-red-600">Error: {error}</p>;
  if (bills.length === 0) return <p>No bills found.</p>;

  return (
    <div className="p-4 bg-white border rounded shadow-md">
      <h2 className="text-2xl font-bold mb-4 text-center">Revenue by Service</h2>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6 justify-center">
        <Select
          value={filterOptions.find((opt) => opt.value === filter)}
          onChange={(opt) => {
            setFilter(opt.value);
            if (opt.value !== "custom") setCustomDate("");
            if (opt.value !== "range") {
              setStartDate("");
              setEndDate("");
            }
          }}
          options={filterOptions}
          placeholder="Select filter"
          styles={baseSelectStyles}
          
        />
        {filter === "custom" && (
          <DatePicker
            selected={customDate ? new Date(customDate) : null}
            onChange={(date) => setCustomDate(date ? date.toISOString().slice(0, 10) : "")}
            customInput={<CustomInput placeholder="Select a date" />}
            placeholderText="Select a date"
          />
        )}
        {filter === "range" && (
          <>
                       <DatePicker
                          selected={startDate ? new Date(startDate) : null}
                          onChange={(date) =>
                            setStartDate(date ? date.toISOString().slice(0, 10) : "")
                          }
                          selectsStart
                          startDate={startDate ? new Date(startDate) : null}
                          endDate={endDate ? new Date(endDate) : null}
                          customInput={<CustomInput placeholder="Start date" />}
                          placeholderText="Start date"
                        />
            <div className="ml-[30px]">
                           <DatePicker
                             selected={endDate ? new Date(endDate) : null}
                             onChange={(date) =>
                               setEndDate(date ? date.toISOString().slice(0, 10) : "")
                             }
                             selectsEnd
                             startDate={startDate ? new Date(startDate) : null}
                             endDate={endDate ? new Date(endDate) : null}
                             minDate={startDate ? new Date(startDate) : null}
                             customInput={<CustomInput placeholder="End date" />}
                             placeholderText="End date"
                           />
            </div>
          </>
        )}
        <button onClick={downloadPDF} className="flex ml-[3rem] primary wide">
          Download PDF
        </button>
      </div>

      {/* Pie Chart */}
      <div className="w-full h-[400px]">
        {chartData.length === 0 ? (
          <p className="text-center text-gray-500 mt-10">
            No paid bills for selected period.
          </p>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={120}
                fill="#8884d8"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => `Kshs ${value.toLocaleString()}`} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default BillsReport;
